/**
 * Mock Mode Utilities
 *
 * Provides utilities to enable mock mode for testing UI without real API calls.
 * When mock mode is enabled, Quick Screen and Deep Dive use mock endpoints
 * that return pre-generated SSE events at configurable speed.
 *
 * Usage:
 *   // In browser console:
 *   localStorage.setItem('ARCEUSX_MOCK_MODE', 'true')
 *   localStorage.setItem('ARCEUSX_MOCK_SPEED', 'fast')  // instant, fast, normal, realistic
 *
 *   // Or via URL:
 *   http://localhost:3002/deals/123?mock=true&speed=fast
 */

export type MockSpeed = 'instant' | 'fast' | 'normal' | 'realistic'

const MOCK_MODE_KEY = 'ARCEUSX_MOCK_MODE'
const MOCK_SPEED_KEY = 'ARCEUSX_MOCK_SPEED'

/**
 * Check if mock mode is enabled via localStorage or URL params
 */
export function isMockModeEnabled(): boolean {
  // Check localStorage
  if (typeof window !== 'undefined') {
    const localStorageValue = localStorage.getItem(MOCK_MODE_KEY)
    if (localStorageValue === 'true') return true

    // Check URL params
    const urlParams = new URLSearchParams(window.location.search)
    if (urlParams.get('mock') === 'true') return true
  }
  return false
}

/**
 * Get the mock speed setting
 */
export function getMockSpeed(): MockSpeed {
  if (typeof window !== 'undefined') {
    // Check URL params first
    const urlParams = new URLSearchParams(window.location.search)
    const urlSpeed = urlParams.get('speed') as MockSpeed | null
    if (urlSpeed && ['instant', 'fast', 'normal', 'realistic'].includes(urlSpeed)) {
      return urlSpeed
    }

    // Check localStorage
    const localStorageSpeed = localStorage.getItem(MOCK_SPEED_KEY) as MockSpeed | null
    if (localStorageSpeed && ['instant', 'fast', 'normal', 'realistic'].includes(localStorageSpeed)) {
      return localStorageSpeed
    }
  }
  return 'fast'  // Default
}

/**
 * Enable mock mode
 */
export function enableMockMode(speed: MockSpeed = 'fast'): void {
  if (typeof window !== 'undefined') {
    localStorage.setItem(MOCK_MODE_KEY, 'true')
    localStorage.setItem(MOCK_SPEED_KEY, speed)
    console.log(`[MockMode] Enabled with speed="${speed}". Reload the page to apply.`)
  }
}

/**
 * Disable mock mode
 */
export function disableMockMode(): void {
  if (typeof window !== 'undefined') {
    localStorage.removeItem(MOCK_MODE_KEY)
    localStorage.removeItem(MOCK_SPEED_KEY)
    console.log('[MockMode] Disabled. Reload the page to apply.')
  }
}

/**
 * Get the API endpoint prefix based on mock mode
 *
 * @param baseUrl - The base API URL
 * @returns The appropriate endpoint prefix
 */
export function getEndpointPrefix(baseUrl: string): string {
  if (isMockModeEnabled()) {
    return `${baseUrl}/api/v1/mock`
  }
  return `${baseUrl}/api/v1`
}

/**
 * Build the diligence start URL
 *
 * @param baseUrl - The base API URL
 * @param dealId - The deal ID
 * @param phase - The phase (quick_screen or deep_dive)
 * @returns The full URL with query params
 */
export function buildDiligenceStartUrl(
  baseUrl: string,
  dealId: string,
  phase: 'quick_screen' | 'deep_dive',
): string {
  const isMock = isMockModeEnabled()
  const prefix = isMock ? `${baseUrl}/api/v1/mock` : `${baseUrl}/api/v1`

  const url = new URL(`${prefix}/deals/${dealId}/diligence`)
  url.searchParams.set('phase', phase)

  if (isMock) {
    url.searchParams.set('speed', getMockSpeed())
  }

  return url.toString()
}

/**
 * Build the SSE stream URL
 *
 * @param baseUrl - The base API URL
 * @param dealId - The deal ID
 * @param taskId - The task ID
 * @param workflowType - The workflow type (quick_screen or deep_dive)
 * @returns The full URL with query params
 */
export function buildStreamUrl(
  baseUrl: string,
  dealId: string,
  taskId: string,
  workflowType: 'quick_screen' | 'deep_dive' = 'deep_dive',
): string {
  const isMock = isMockModeEnabled()
  const prefix = isMock ? `${baseUrl}/api/v1/mock` : `${baseUrl}/api/v1`

  const url = new URL(`${prefix}/deals/${dealId}/diligence/stream/${taskId}`)

  if (!isMock) {
    url.searchParams.set('workflow_type', workflowType)
  } else {
    url.searchParams.set('speed', getMockSpeed())
  }

  return url.toString()
}

// Expose to window for easy console access during development
if (typeof window !== 'undefined') {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (window as any).mockMode = {
    enable: enableMockMode,
    disable: disableMockMode,
    isEnabled: isMockModeEnabled,
    getSpeed: getMockSpeed,
  }
}
